import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, formatMass, formatNumber, titleCase } from '../../lib/format'
import type { ListResponse, ProductionLog } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Skeleton, StatusBadge,
} from '../../components/ui'
import { ChartIcon } from '../../components/icons'

const FILTERS = [
  { value: '', label: 'All' },
  { value: 'submitted', label: 'Awaiting review' },
  { value: 'approved', label: 'Approved' },
  { value: 'flagged', label: 'Flagged' },
]

export default function ProductionReview() {
  const qc = useQueryClient()
  const [status, setStatus] = useState('submitted')
  const [openId, setOpenId] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'production', status],
    queryFn: () => api.get<ListResponse<ProductionLog>>(
      `/leader/production${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  })

  const review = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'flag' }) =>
      api.post(`/leader/production/${id}/review`, { action, note }),
    onSuccess: () => {
      // The dashboard KPI counts the same records.
      void qc.invalidateQueries({ queryKey: ['leader'] })
      setOpenId(null)
      setNote('')
    },
  })

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Production review</h1>
          <p className="page-head__sub">
            Approve or flag output logged by your cooperative members.
          </p>
        </div>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        {FILTERS.map((f) => (
          <Button key={f.value} size="sm"
                  variant={status === f.value ? 'primary' : 'secondary'}
                  onClick={() => setStatus(f.value)}>
            {f.label}
          </Button>
        ))}
      </div>

      {review.isError && (
        <Alert tone="danger">{(review.error as Error).message}</Alert>
      )}

      <Card>
        <CardHeader title="Logs"
                    sub={items.length ? `${items.length} record${items.length === 1 ? '' : 's'}` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="76%" /><Skeleton h={20} w="62%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={<ChartIcon size={22} />}
                        title={status === 'submitted' ? 'Nothing awaiting review' : 'No matching logs'}
                        body={status === 'submitted'
                          ? 'Every submitted log has been reviewed.'
                          : 'Try a different filter.'} />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Mineral</th><th className="table__num">Volume</th><th>Period</th>
                    <th>Buyer</th><th className="table__num">Value</th><th>Status</th><th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((p, i) => {
                    const id = p.id ?? String(i)
                    const pending = (p.status || 'submitted') === 'submitted'
                    return (
                      // Keyed Fragment: the fragment is the array item, so the
                      // key belongs here rather than on the rows inside it.
                      <Fragment key={id}>
                        <tr>
                          <td>{titleCase(p.mineral_type)}</td>
                          <td className="table__num">{formatMass(p.volume_kg)}</td>
                          <td>{formatDate(p.period_start)}</td>
                          <td>{p.buyer_name || '—'}</td>
                          <td className="table__num">{formatNumber(p.value_local, 2)}</td>
                          <td><StatusBadge status={p.status || 'submitted'} /></td>
                          <td className="table__num">
                            {pending && p.id && (
                              <Button size="sm" variant="secondary"
                                      onClick={() => { setOpenId(openId === id ? null : id); setNote('') }}>
                                {openId === id ? 'Close' : 'Review'}
                              </Button>
                            )}
                          </td>
                        </tr>
                        {openId === id && p.id && (
                          <tr>
                            <td colSpan={7} style={{ background: 'var(--bg-sunken)' }}>
                              <div className="stack" style={{ padding: 'var(--sp-2) 0' }}>
                                {p.notes && (
                                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                                    <strong>Miner&rsquo;s note:</strong> {p.notes}
                                  </div>
                                )}
                                <Input label="Review note (optional)"
                                       placeholder="Sent to the miner with your decision"
                                       value={note}
                                       onChange={(e) => setNote(e.target.value)} />
                                <div className="row">
                                  <Button size="sm" loading={review.isPending}
                                          onClick={() => review.mutate({ id: p.id!, action: 'approve' })}>
                                    Approve
                                  </Button>
                                  <Button size="sm" variant="danger" loading={review.isPending}
                                          onClick={() => review.mutate({ id: p.id!, action: 'flag' })}>
                                    Flag
                                  </Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
